import React, { useRef, useState, useEffect }  from 'react';
import { 
    Editor, 
    EditorState, 
    RichUtils, 
    getDefaultKeyBinding, 
    DefaultDraftBlockRenderMap, 
    CompositeDecorator,
    ContentState,
    convertFromHTML,
    Modifier
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import { SketchPicker } from 'react-color'
import { makeStyles } from '@material-ui/core/styles';
import Immutable from 'immutable';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Tooltip from '@material-ui/core/Tooltip';
import InputBase from '@material-ui/core/InputBase';
import Popover from '@material-ui/core/Popover';
import SvgIcon from '@material-ui/core/SvgIcon';
import Collapse from '@material-ui/core/Collapse';

import FormatClearIcon from '@material-ui/icons/FormatClear';

/// Custom Controle
import TextAlignControls from 'app/components/rte/Controls/TextAlign';
import ListControls from 'app/components/rte/Controls/List';
import LinkControls from 'app/components/rte/Controls/Link';
import InlineStyleControls from 'app/components/rte/Controls/InlineStyle';
import ColorControls from 'app/components/rte/Controls/Color';
import FBGColorControls from 'app/components/rte/Controls/FBGColor';
import FontSizeControls from 'app/components/rte/Controls/FontSize';
import FontFamilyControls from 'app/components/rte/Controls/FontFamily';

import Fonts from 'app/utils/Fonts';

const useStyles = makeStyles(theme => ({
    container: {

    },
    containerWithBorder: {
        border: `1px solid #ddd`,
        borderRadius: theme.shape.borderRadius
    },
    toolbar: {
        padding: theme.spacing(1),
        backgroundColor: '#fff',
        borderTop: `1px solid #ddd`,
        borderBottom: `1px solid #ddd`
    },
    root: props => ({
        height: 'auto',
        fontSize: 14,
        fontFamily: '"Helvetica", "Arial", sans-serif',
        whiteSpace: "pre-wrap",

        "& .public-DraftStyleDefault-block": {
            marginBlockStart: '1em',
            marginBlockEnd: '1em'
        },
        "& ol .public-DraftStyleDefault-block": {
            marginBlockStart: 0,
            marginBlockEnd: 0
        },
        "& .public-DraftEditor-content": {
            minHeight: props.editorMinHeight
        },
        "& .style-align-left div": {
            textAlign: 'left'
        },
        "& .style-align-center div": {
            textAlign: 'center'
        },
        "& .style-align-right div": {
            textAlign: 'right'
        },
        "& .style-align-justify div": {
            textAlign: 'justify'
        },
    }),
    marginLeft: {
        marginLeft: theme.spacing(3)
    },
    marginTop: {
        marginTop: theme.spacing(2)
    },
    alignCenter: {
        textAlign: 'center'
    },
    alignRight: {
        textAlign: 'right'
    },
    alignLeft: {
        textAlign: 'left'
    },
    justifyText: {
        textAlign: 'justify'
    },
    onButton: {
        backgroundColor: 'rgba(0, 0, 0, 0.10);',
        "&:hover" : {
            backgroundColor: 'background-color: rgba(0, 0, 0, 0.10);',
        }
    },
    headerOne: {
        // fontSize: '125px',
    },
    headerTwo: {

    },
    headerThree: {

    },
    headerFour: {

    },
    paragraph: {

    },
    richTextEditor: {
        padding: theme.spacing(3),
    },
    buttonGroupBorder: {
        border: `1px solid rgba(0, 0, 0, 0.23)`,
        borderRadius: theme.shape.borderRadius,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        paddingTop: 1,
        paddingBottom: 1
    },
    input: {
        minWidth: 'auto',

        '& input[type=number]': {
            textAlign: 'center',
            width: 30,
            '-webkit-appearance': 'none',
            margin: 0
        },
        '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0
        }
    },
    link: {
        color: '#3b5998',
        textDecoration: 'underline',
      },
}));

const sizes = [
    'Header',
    'Header 2',
    'Subheader',
    'Paragraph',
    'Paragraph 2 (no styling)',
]

const styleMap = {}

const blockRenderMap = Immutable.Map({
    'br': {
        element: 'br'
    },
    a: {
        element: 'a'
    }
});

const extendedBlockRenderMap = DefaultDraftBlockRenderMap.merge(blockRenderMap);

// Add fonts to custom style map
Object.assign(styleMap, Fonts);

export default function RichTextEditor(props) {

    const { editorState, setEditorState, options, handleOptions, active } = props;

    const background_color = options?.background_color ?? "ffffff";
    const padding_top = options?.padding_top ?? 24;
    const padding_left = options?.padding_left ?? 24;
    const padding_right = options?.padding_right ?? 24;
    const padding_bottom = options?.padding_bottom ?? 24;
    const editor_min_height = options?.editor_min_height ?? 0;
    const editor_border = options?.editor_border || false;

    const classes = useStyles({ editorMinHeight: editor_min_height });
    const editorRef = useRef(null);

    const [ colorPickerAnchorEl, setColorPickerAnchorEl ] = useState(null);

    const onChange = (es) => setEditorState(es);
    const focus = () => editorRef.current.focus();
    const handlingOptions = Boolean(handleOptions);

    useEffect(() => {
        const decorators = new CompositeDecorator([
            {
                strategy: findLinkEntities,
                component: Link,
            },
        ]);

        const newEditorState = EditorState.set(editorState, {decorator: decorators});

        onChange(newEditorState);
    }, [])

    const handleKeyCommand = (command, es) => {
        
        const newState = RichUtils.handleKeyCommand(es, command);
        if (newState) {
          onChange(newState);
          return true;
        }
        return false;
    };

    const mapKeyToEditorCommand = (e) => {
        
        // Shift Enter inserts soft new line in same block
        if (e.keyCode === 13 && e.nativeEvent.shiftKey) {
            const newEditorState = RichUtils.insertSoftNewline(editorState);

            if (newEditorState !== editorState) onChange(newEditorState);

            return null;
        }

        // Tab actually tabs out (max depth of 4) instead of switching to next input
        if (e.keyCode === 9) {
            const newEditorState = RichUtils.onTab(e, editorState, 4 /* maxDepth */);

            if (newEditorState !== editorState) onChange(newEditorState);

            e.preventDefault();
            return null;
        }

        return getDefaultKeyBinding(e);
    };

    const getBlockStyle = block => {
        switch (block.getType()) {
            case "left":
                return "style-align-left";
            case "center":
                return "style-align-center";
            case "right":
                return "style-align-right";
            case "justify":
                return "style-justify";
            default:
                return null;
        }
    };

    const customStyleFn = style => {
        const styleNames = style.toJS();

        return styleNames.reduce((styles, styleName) => {
           
            if(styleName.startsWith('COLOR_')) {
                styles.color = styleName.split('COLOR_')[1];
            }

            if(styleName.startsWith('FBG_COLOR_')) {
                styles.backgroundColor = styleName.split('FBG_COLOR_')[1];
            }

            if(styleName.startsWith('FONTSIZE_')) {
                styles.fontSize = styleName.split('FONTSIZE_')[1] + 'px';
            }

            return styles;
        }, {});
    }

    const toggleBlockType = blockType => onChange(RichUtils.toggleBlockType(editorState, blockType));

    const toggleInlineStyle = inlineStyle => onChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));

    const toggleCustomStyle = (value, key) => {

		const styles = editorState.getCurrentInlineStyle().toJS();

		let newState = styles.reduce((state, styleKey) => {
			if (styleKey.startsWith(key)) {
                return RichUtils.toggleInlineStyle(state, styleKey);
            }
			return state;
		}, editorState);

		if(value != null) {
            if (key !== 'FONTFAMILY_') value = key + value;

			newState = RichUtils.toggleInlineStyle(newState, value);
		}

		onChange(newState);
    }

    const toggleLink = (urlValue) => {
       
        const contentState = editorState.getCurrentContent();
        const contentStateWithEntity = contentState.createEntity(
          'LINK',
          'MUTABLE',
          {url: urlValue}
        );
        const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

        const newEditorState = EditorState.set(editorState, { currentContent: contentStateWithEntity });

        onChange(RichUtils.toggleLink(newEditorState, newEditorState.getSelection(), entityKey));
    }

    const clearStyles = (event) => {
        event.preventDefault();

		const styles = editorState.getCurrentInlineStyle().toJS();

		let newState = styles.reduce((state, styleKey) => {
			return RichUtils.toggleInlineStyle(state, styleKey);
		}, editorState);

		onChange(newState);
    }

    // Non editorContent related items

    const handlePadding = (e, name) => {
        if (handlingOptions) handleOptions(name, parseInt(e.target.value) || 0);
    }

    const handleColor = (color) => {
        if (color && color.hex && handlingOptions) {
            handleOptions('background_color', color.hex);
        }
    }

    // Color Picker Popover....................

    const openColorPicker = (event) => {
        event.preventDefault();
        if (handlingOptions) setColorPickerAnchorEl(event.currentTarget);
    }

    const closeColorPicker= () => {
        setColorPickerAnchorEl(null);
    }

    // Link Functions -------------------------

    const findLinkEntities = (contentBlock, callback, contentState) => {
        contentBlock.findEntityRanges(
            (character) => {
                const entityKey = character.getEntity();
                return (
                    entityKey !== null &&
                    contentState.getEntity(entityKey).getType() === 'LINK'
                );
            },
            callback
        );
    }
    
    const Link = (props) => {
        const {url} = props.contentState.getEntity(props.entityKey).getData();
        return (
            <a href={url} className={classes.link}>
                {props.children}
            </a>
        );
    };

    // Paste Handling -------------------------

    const handlePastedText = (text, html, editorState) => {
        
        const newHtml = html.replace(/<br>/g, '<br/>');

        const blocksFromHTML = convertFromHTML(newHtml);
        
        const newHtmlContent = ContentState.createFromBlockArray(
            blocksFromHTML.contentBlocks,
            blocksFromHTML.entityMap,
        );

        const modifiedContent = Modifier.replaceWithFragment(
            editorState.getCurrentContent(), 
            editorState.getSelection(),
            newHtmlContent.blockMap
        );

        onChange(EditorState.push(editorState, modifiedContent, 'insert-fragment'));

        return true;
    }

    // Constants ---------------------

    const selectAll = (e) => e.target.select();

    return (
        <div className={editor_border ? classes.containerWithBorder : classes.container}>
            <Collapse in={active}>
                <div className={classes.toolbar}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item>
                            <InlineStyleControls editorState={editorState} onToggle={toggleInlineStyle} />
                        </Grid>
                        <Grid item>
                            <TextAlignControls editorState={editorState} onToggle={toggleBlockType} />
                        </Grid>
                        <Grid item>
                            <ListControls editorState={editorState} onToggle={toggleBlockType} />
                        </Grid>
                        <Grid item>
                            <ColorControls editorState={editorState} onToggle={toggleCustomStyle} editorFocus={focus} />
                        </Grid>
                        <Grid item>
                            <FBGColorControls editorState={editorState} onToggle={toggleCustomStyle} editorFocus={focus} />
                        </Grid>
                        <Grid item>
                            <LinkControls editorState={editorState} onLink={toggleLink} editorFocus={focus} />
                        </Grid>
                        {/* <Grid item>
                            <Tooltip title="Insert Image">
                                <Button variant="outlined" size="small">
                                    <ImageIcon/>
                                </Button>
                            </Tooltip>
                        </Grid> */}
                        <Grid item>
                            <FontSizeControls editorState={editorState} onToggle={toggleCustomStyle} editorFocus={focus} />
                        </Grid>
                        <Grid item>
                            <FontFamilyControls editorState={editorState} onToggle={toggleCustomStyle} editorFocus={focus} />
                        </Grid>
                        <Grid item>
                            <ButtonGroup>
                                <Tooltip title="Clear Formatting">
                                    <Button
                                        size="small"
                                        onMouseDown={clearStyles}
                                        >
                                        <FormatClearIcon/>
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Grid>
                        <Grid item xs={1}/>
                        <Grid item>
                            <div className={classes.buttonGroupBorder}>
                                <Tooltip title="Padding Top">
                                    <InputBase
                                        value={padding_top}
                                        onChange={(e) => handlePadding(e, 'padding_top')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                        disabled={! handlingOptions}
                                    />
                                </Tooltip>
                                <Tooltip title="Padding Bottom">
                                    <InputBase
                                        value={padding_bottom}
                                        onChange={(e) => handlePadding(e, 'padding_bottom')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                        disabled={! handlingOptions}
                                    />
                                </Tooltip>
                                <Tooltip title="Padding Left">
                                    <InputBase
                                        value={padding_left}
                                        onChange={(e) => handlePadding(e, 'padding_left')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                        disabled={! handlingOptions}
                                    />
                                </Tooltip>
                                <Tooltip title="Padding Right">
                                    <InputBase
                                        value={padding_right}
                                        onChange={(e) => handlePadding(e, 'padding_right')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                        disabled={! handlingOptions}
                                    />
                                </Tooltip>
                            </div>
                        </Grid>
                        <Grid item>
                            <ButtonGroup>
                                <Tooltip title="Background Color">
                                    <span>
                                        <Button
                                            className={classes.btn}
                                            aria-label="Background Color"
                                            size="small"
                                            onMouseDown={event => openColorPicker(event)}
                                            disabled={! handlingOptions}
                                            >
                                            <SvgIcon>
                                                <path d="M16.56 8.94L7.62 0 6.21 1.41l2.38 2.38-5.15 5.15c-.59.59-.59 1.54 0 2.12l5.5 5.5c.29.29.68.44 1.06.44s.77-.15 1.06-.44l5.5-5.5c.59-.58.59-1.53 0-2.12zM5.21 10L10 5.21 14.79 10H5.21zM19 11.5s-2 2.17-2 3.5c0 1.1.9 2 2 2s2-.9 2-2c0-1.33-2-3.5-2-3.5z"/>
                                                <path style={{ color: background_color }} d="M0 20h24v4H0z"/>
                                            </SvgIcon>
                                        </Button>
                                    </span>
                                </Tooltip>
                            </ButtonGroup>
                            <Popover
                                open={Boolean(colorPickerAnchorEl)}
                                anchorEl={colorPickerAnchorEl}
                                onClose={closeColorPicker}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                                disableAutoFocus
                                disableEnforceFocus
                                disableRestoreFocus
                                >
                                <SketchPicker color={background_color} onChange={handleColor} disableAlpha />
                            </Popover>
                        </Grid>
                    </Grid>
                </div>
            </Collapse>
            <div className={classes.root} onClick={focus} style={{
                backgroundColor: background_color,
                paddingTop: padding_top,
                paddingBottom: padding_bottom,
                paddingLeft: padding_left,
                paddingRight: padding_right,
                }}
                >
                <Editor
                    editorState={editorState}
                    onChange={onChange}
                    handleKeyCommand={handleKeyCommand}
                    keyBindingFn={mapKeyToEditorCommand}
                    blockStyleFn={getBlockStyle}
                    customStyleFn={customStyleFn}
                    customStyleMap={styleMap}
                    ref={editorRef}
                    preserveSelectionOnBlur={true}
                    spellCheck={true}
                    blockRenderMap={extendedBlockRenderMap}
                    handlePastedText={handlePastedText}
                />
            </div>
        </div>
    )
}

